// Header token/session pill — pure broker presentation helpers.
//
// Backend `token_status` (ACTIVE / non-ACTIVE) is the sole daily-lifecycle
// authority here; this module never infers validity from a date. The header
// action for every broker in HEADER_OAUTH_BROKER_NAMES reuses the existing
// brokerApi.getOAuthUrl(BROKER) -> redirect(oauth_url) flow already used by
// BrokerPage.tsx; no second Alice login/session API is introduced.

// Brokers whose header/mobile-sheet action redirects through the existing
// OAuth login-URL flow (brokerApi.getOAuthUrl -> redirect to oauth_url).
export const HEADER_OAUTH_BROKER_NAMES = new Set([
  'fyers',
  'zerodha',
  'nuvama',
  'mastertrust',
  'aliceblue',
]);

export function isHeaderOAuthBroker(brokerName) {
  return HEADER_OAUTH_BROKER_NAMES.has(brokerName);
}

// Dependency-injected so it is directly testable without a browser: the
// real Header.jsx caller passes brokerApi.getOAuthUrl and a redirect that
// sets window.location.href, but nothing here touches `window` itself.
// Self-gates on HEADER_OAUTH_BROKER_NAMES so AngelOne/KotakNeo — which keep
// their own distinct header branches — can never reach getOAuthUrl/redirect
// through this helper even if called directly.
export async function performHeaderOAuthRedirect({ brokerName, getOAuthUrl, redirect }) {
  if (!HEADER_OAUTH_BROKER_NAMES.has(brokerName)) return null;
  const data = await getOAuthUrl(brokerName.toUpperCase());
  if (data?.oauth_url) redirect(data.oauth_url);
  return data ?? null;
}

// Alice Blue gets session-specific wording; every other broker keeps its
// existing generic "token" wording unchanged. An ACTIVE-but-unhealthy Alice
// session reads "Session unavailable" (not "Session error"), because the
// backend already owns authoritative expiry — a merely unavailable funds
// probe on an ACTIVE session is an operational read failure, not a token
// error requiring regeneration. Every other broker keeps its existing
// "Session error" wording for that same ACTIVE-but-unhealthy state.
export function resolveHeaderTokenLabel({
  brokerName,
  canGenerateToken,
  isChecking,
  storedTokenStatus,
  isTokenValid,
}) {
  const isAlice = brokerName === 'aliceblue';

  if (!canGenerateToken) return 'No broker';
  if (isChecking) return 'Checking…';
  if (storedTokenStatus !== 'ACTIVE') return isAlice ? 'Generate session' : 'Generate token';
  if (isTokenValid) return isAlice ? 'Session active' : 'Token active';
  return isAlice ? 'Session unavailable' : 'Session error';
}

// Actionability is a separate concern from health/label wording: the
// generate/session action must be reachable ONLY when the stored backend
// lifecycle itself requires regeneration (non-ACTIVE). An ACTIVE session
// whose live funds-health probe is merely unavailable/erroring must never
// become actionable — that would let a transient/unknown probe failure
// trigger a real OAuth/testConnection/navigate action. Deliberately takes
// no brokerName/funds-health parameter: actionability is broker-agnostic.
export function isHeaderTokenActionable({ canGenerateToken, storedTokenStatus }) {
  return Boolean(canGenerateToken) && storedTokenStatus !== 'ACTIVE';
}
